import type { AiChatConversation } from "@/api";

// Mirrors StaleProcessingTTL in back/aichat/db/db.go. The owning handler
// refreshes processing_started_at every 15s, so a live turn always looks
// fresh; a lock older than this belongs to a crashed task.
export const STALE_PROCESSING_MS = 3 * 60 * 1000;

export type ReadyEntry = { id: string; title: string; finishedAt: Date };

// A turn that ended without an answer while the user was away. "stopped" is a
// Stop from somewhere else (another tab); "failed" is an error or a crash.
export type FailedEntry = ReadyEntry & { outcome: "stopped" | "failed" };

export type ActivityState = {
  runningIds: ReadonlySet<string>;
  // id -> when the running turn started, to tell whether a Stop request came
  // in during that turn.
  runningSince: ReadonlyMap<string, number>;
  // id -> when this browser stopped the turn or watched it finish. Either
  // way the user already knows the outcome, so it never becomes ready.
  stopped: ReadonlyMap<string, number>;
  ready: ReadyEntry[];
  failed: FailedEntry[];
  // Dismiss clears the nav's ready state only. The ready entries stay, so chat
  // history keeps marking each finished chat until it is opened.
  readyDismissed: boolean;
  activeId: string | null;
};

export type ActivityAction =
  | { type: "list"; conversations: AiChatConversation[]; now: number }
  | { type: "stop"; id: string; now: number }
  | { type: "seen"; id: string; now: number }
  | { type: "started"; id: string }
  | { type: "active"; id: string | null }
  | { type: "dismiss" };

export type ChatStatus = "running" | "ready" | "stopped" | "failed";

export function chatStatuses({
  runningIds,
  ready,
  failed,
}: Pick<ActivityState, "runningIds" | "ready" | "failed">): Map<
  string,
  ChatStatus
> {
  const statuses = new Map<string, ChatStatus>();
  for (const f of failed) statuses.set(f.id, f.outcome);
  for (const r of ready) statuses.set(r.id, "ready");
  for (const id of runningIds) statuses.set(id, "running");
  return statuses;
}

export const initialActivityState: ActivityState = {
  runningIds: new Set(),
  runningSince: new Map(),
  stopped: new Map(),
  ready: [],
  failed: [],
  readyDismissed: false,
  activeId: null,
};

export function isRunning(c: AiChatConversation, now: number): boolean {
  if (c.processing_status !== "processing" || !c.processing_started_at) {
    return false;
  }
  return now - Date.parse(c.processing_started_at) < STALE_PROCESSING_MS;
}

function failedOutcome(
  c: AiChatConversation,
  startedAt: number | undefined,
): FailedEntry["outcome"] | null {
  const stopRequested =
    c.cancel_requested_at &&
    startedAt !== undefined &&
    Date.parse(c.cancel_requested_at) >= startedAt;
  if (stopRequested || c.processing_status === "cancelled") return "stopped";
  // A row still marked processing has fallen out of runningIds because its
  // lock went stale, which means the task crashed.
  if (c.processing_status === "error" || c.processing_status === "processing") {
    return "failed";
  }
  return null;
}

function reconcileList(
  state: ActivityState,
  conversations: AiChatConversation[],
  now: number,
): ActivityState {
  const byId = new Map(conversations.map((c) => [c.id, c]));
  const running = conversations.filter((c) => isRunning(c, now));
  const runningIds = new Set(running.map((c) => c.id));
  // The owner refreshes processing_started_at as a heartbeat, so keep the
  // first value seen: a Stop just before a refresh still falls in the turn.
  const runningSince = new Map(
    running.map((c) => [
      c.id,
      state.runningSince.get(c.id) ?? Date.parse(c.processing_started_at!),
    ]),
  );
  const stopped = new Map(state.stopped);
  // A chat missing from the list was deleted, so its entry would link to a
  // dead page. The list holds the newest chats, and a chat that just finished
  // is among them.
  const current = (e: ReadyEntry) => byId.has(e.id) && !runningIds.has(e.id);
  let ready = state.ready.filter(current);
  let failed = state.failed.filter(current);
  let readyDismissed = state.readyDismissed;

  for (const id of state.runningIds) {
    if (runningIds.has(id)) continue;
    const c = byId.get(id);
    const unseen = c !== undefined && id !== state.activeId && !stopped.has(id);
    stopped.delete(id);
    if (!unseen) continue;

    const entry = { id, title: c.title, finishedAt: new Date(now) };
    const outcome = failedOutcome(c, state.runningSince.get(id));
    if (outcome) {
      failed = [{ ...entry, outcome }, ...failed.filter((f) => f.id !== id)];
    } else {
      ready = [entry, ...ready.filter((r) => r.id !== id)];
      readyDismissed = false;
    }
  }

  for (const [id, stoppedAt] of stopped) {
    if (!runningIds.has(id) && now - stoppedAt > STALE_PROCESSING_MS) {
      stopped.delete(id);
    }
  }

  return {
    ...state,
    runningIds,
    runningSince,
    stopped,
    ready,
    failed,
    readyDismissed,
  };
}

export function activityReducer(
  state: ActivityState,
  action: ActivityAction,
): ActivityState {
  switch (action.type) {
    case "list":
      return reconcileList(state, action.conversations, action.now);
    // This browser's send was acked, so a new turn began. A queued follow-up
    // can start before any poll sees the chat idle, so marks from the
    // previous turn must not carry over into this one. The next poll records
    // the turn's start from the server, whose clock also stamps Stop requests.
    case "started": {
      const stopped = new Map(state.stopped);
      stopped.delete(action.id);
      const runningSince = new Map(state.runningSince);
      runningSince.delete(action.id);
      return { ...state, stopped, runningSince };
    }
    case "stop":
    case "seen":
      return {
        ...state,
        stopped: new Map(state.stopped).set(action.id, action.now),
      };
    case "active":
      return {
        ...state,
        activeId: action.id,
        ready: state.ready.filter((r) => r.id !== action.id),
        failed: state.failed.filter((f) => f.id !== action.id),
      };
    case "dismiss":
      return { ...state, readyDismissed: true };
  }
}
